import { Buffer } from "buffer";
import { EAuthenticationType } from "@kuika/kuika-cl-model";
import {
  DraftRowBlankTest,
  ensureTrailingDraftRow,
  isBlankText,
  withoutBlankRows
} from "../../shared/kuika-edit-grid/draft-rows";
import { REST_AUTH_TYPE_LABELS } from "../rest-connection-specs";

/**
 * The header table an API datasource carries, in both directions: the flat `Record` the DTO
 * stores, and the rows the form edits.
 *
 * This lived four times over — in the OData, Swagger and REST-collection connection forms and
 * in the endpoint form. Three of those copies were identical; the drift in the other two was
 * silent and is corrected here, see `parseHeaderRecord`. (A fifth transcription sat in a
 * `data-source-modal` nothing had imported for years; it is gone.)
 */

/** `type` is one of these four. Kept as `string` because the table's Select hands one over. */
export const PARAMETER_TYPE_CUSTOM: string = "custom";
export const PARAMETER_TYPE_USERNAME: string = "username";
export const PARAMETER_TYPE_AUTH_TOKEN: string = "authtoken";
export const PARAMETER_TYPE_BASIC_AUTH: string = "basicAuth";

/** Placeholders the backend substitutes per request; they are stored, not resolved here. */
export const CURRENT_USER_NAME_TOKEN: string = "{CurrentUserName}";
export const AUTHORIZATION_TOKEN: string = "{AuthorizationToken}";
/** Shown in place of the encoded credentials, which live in their own two fields. */
export const BASIC_AUTH_TOKEN: string = "{BasicAuthToken}";
export const AUTHORIZATION_HEADER_KEY: string = "Authorization";

export interface IApiKeyParameter {
  id: number;
  type: string;
  key: string;
  value: string;
}

export interface IBasicAuthCredentials {
  username: string;
  password: string;
}

const emptyCredentials = (): IBasicAuthCredentials => ({ username: "", password: "" });

/** The header table's trailing row — where the next header is typed. */
export const blankHeaderParameter = (): IApiKeyParameter => ({
  id: 0,
  type: PARAMETER_TYPE_CUSTOM,
  key: "",
  value: ""
});

/**
 * A header row nothing has been typed into: the table's draft row, or a custom row cleared back to
 * nothing. The type counts — a generated row (Bearer's current-user row) starts with an empty key
 * and is still a row, and picking a type on the draft is what makes it one.
 */
export const isBlankHeaderParameter: DraftRowBlankTest<IApiKeyParameter> = (
  parameter: IApiKeyParameter | undefined
): boolean =>
  (!parameter?.type || parameter.type === PARAMETER_TYPE_CUSTOM) &&
  isBlankText(parameter?.key) &&
  isBlankText(parameter?.value);

export const generateBasicAuthToken = (username?: string, password?: string): string => {
  if (!username || !password) return "";
  return `Basic ${btoa(`${username}:${password}`)}`;
};

export const decodeBasicAuthCredentials = (tokenValue?: string): IBasicAuthCredentials => {
  if (!tokenValue) return emptyCredentials();

  const token: string = tokenValue.replace(/Basic /gi, "");
  const [username, password] = Buffer.from(token, "base64").toString("utf-8").split(":");
  return { username: username ?? "", password: password ?? "" };
};

/**
 * Form rows to the `Record` the DTO stores. Blank rows — the table's trailing draft, a row cleared
 * back to nothing — are not headers: stored as they are they became a `"": ""` entry.
 */
export const buildHeaderRecord = (
  parameters: IApiKeyParameter[] | undefined,
  credentials: IBasicAuthCredentials
): Record<string, string> => {
  const headers: Record<string, string> = {};

  withoutBlankRows(parameters, isBlankHeaderParameter).forEach((parameter: IApiKeyParameter): void => {
    if (parameter.type === PARAMETER_TYPE_USERNAME) {
      headers[parameter.key] = CURRENT_USER_NAME_TOKEN;
    } else if (parameter.type === PARAMETER_TYPE_AUTH_TOKEN) {
      headers[parameter.key] = AUTHORIZATION_TOKEN;
    } else if (parameter.type === PARAMETER_TYPE_BASIC_AUTH) {
      headers[parameter.key] = generateBasicAuthToken(credentials.username, credentials.password);
    } else {
      headers[parameter.key] = parameter.value;
    }
  });

  return headers;
};

export interface IParsedHeaderParameters {
  parameters: IApiKeyParameter[];
  /** Decoded out of the `Authorization` header, empty unless the datasource uses Basic. */
  basicAuth: IBasicAuthCredentials;
}

/**
 * The `Record` the DTO stores back to form rows.
 *
 * Two copies disagreed with this, both silently:
 *
 * - the REST-collection form matched `Authorization` without checking the auth type, so a
 *   datasource holding a literal bearer token in that header had it base64-decoded into the
 *   Basic username/password fields and re-saved as `Basic …` — the header was rewritten by
 *   opening the form. Deciding on the auth type, as the OData and Swagger copies did, is what
 *   this keeps.
 * - the endpoint form read `headerKeys[element]` — the `Object.keys` ARRAY indexed by a string
 *   key, which is always `undefined`. Every custom header loaded blank and no row ever came
 *   back as username or authtoken.
 */
export const parseHeaderRecord = (
  headers: Record<string, string> | undefined,
  authType: EAuthenticationType
): IParsedHeaderParameters => {
  const parameters: IApiKeyParameter[] = [];
  let basicAuth: IBasicAuthCredentials = emptyCredentials();

  if (!headers) return { parameters, basicAuth };

  Object.keys(headers).forEach((key: string, index: number): void => {
    const value: string = headers[key];

    if (value === CURRENT_USER_NAME_TOKEN) {
      parameters.push({ id: index, key, value, type: PARAMETER_TYPE_USERNAME });
      return;
    }
    if (value === AUTHORIZATION_TOKEN) {
      parameters.push({ id: index, key, value, type: PARAMETER_TYPE_AUTH_TOKEN });
      return;
    }
    if (key === AUTHORIZATION_HEADER_KEY && authType === EAuthenticationType.Basic) {
      basicAuth = decodeBasicAuthCredentials(value);
      parameters.push({
        id: index,
        key: AUTHORIZATION_HEADER_KEY,
        value: BASIC_AUTH_TOKEN,
        type: PARAMETER_TYPE_BASIC_AUTH
      });
      return;
    }

    parameters.push({ id: index, key, value, type: PARAMETER_TYPE_CUSTOM });
  });

  return { parameters, basicAuth };
};

/** Which row holds each of the three generated headers; -1 when none does. */
export interface IGeneratedHeaderRowIndexes {
  userNameHeaderParameterIndex: number;
  authTokenHeaderParameterIndex: number;
  basicAuthHeaderParameterIndex: number;
}

export interface IOrganizedHeaderParameters extends IGeneratedHeaderRowIndexes {
  parameters: IApiKeyParameter[];
}

/**
 * Whether a row's value is written by the auth type rather than typed — the current user's
 * name, the authorization token, the Basic credentials. The table locks that row's value.
 */
export const isGeneratedHeaderRow = (indexes: IGeneratedHeaderRowIndexes, rowIndex: number): boolean =>
  rowIndex > -1 &&
  (indexes.userNameHeaderParameterIndex === rowIndex ||
    indexes.authTokenHeaderParameterIndex === rowIndex ||
    indexes.basicAuthHeaderParameterIndex === rowIndex);

/**
 * The locked value's tooltip: which auth type fills it in. A generated row can survive onto
 * a type that generates nothing (a stored `{CurrentUserName}` header parses back as one under
 * No Auth), and "Filled in from No Auth" would be false — Kuika still substitutes it per call.
 */
export const describeGeneratedHeaderSource = (authType: EAuthenticationType): string => {
  const generatesHeaders: boolean =
    authType === EAuthenticationType.KuikaAuth ||
    authType === EAuthenticationType.Basic ||
    authType === EAuthenticationType.ClientIdSecret;
  if (!generatesHeaders) return "Filled in by Kuika on every call";
  return `Filled in from ${REST_AUTH_TYPE_LABELS[authType]}`;
};

/**
 * Keeps the table's own rules: each of the three generated rows may appear once, a duplicate
 * falls back to a custom row, and a custom row may not hold a placeholder it did not earn.
 * The indices are what the table uses to disable the options already taken.
 */
export const organizeHeaderParameters = (parameters: IApiKeyParameter[]): IOrganizedHeaderParameters => {
  let userNameHeaderParameterIndex: number = -1;
  let authTokenHeaderParameterIndex: number = -1;
  let basicAuthHeaderParameterIndex: number = -1;
  const organized: IApiKeyParameter[] = [];

  parameters.forEach((parameter: IApiKeyParameter, index: number): void => {
    if (!parameter.type) return;

    let key: string = parameter.key;
    let value: string = parameter.value;
    let type: string = parameter.type;

    if (type === PARAMETER_TYPE_BASIC_AUTH) {
      if (basicAuthHeaderParameterIndex > -1) {
        type = PARAMETER_TYPE_CUSTOM;
        key = "";
        value = "";
      } else {
        key = AUTHORIZATION_HEADER_KEY;
        value = BASIC_AUTH_TOKEN;
        basicAuthHeaderParameterIndex = index;
      }
    } else if (type === PARAMETER_TYPE_USERNAME) {
      if (userNameHeaderParameterIndex > -1 && value === CURRENT_USER_NAME_TOKEN) {
        type = PARAMETER_TYPE_CUSTOM;
        value = "";
      } else {
        value = CURRENT_USER_NAME_TOKEN;
        userNameHeaderParameterIndex = index;
      }
    } else if (type === PARAMETER_TYPE_AUTH_TOKEN) {
      if (authTokenHeaderParameterIndex > -1 && value === AUTHORIZATION_TOKEN) {
        type = PARAMETER_TYPE_CUSTOM;
        value = "";
      } else {
        value = AUTHORIZATION_TOKEN;
        authTokenHeaderParameterIndex = index;
      }
    } else if (value === CURRENT_USER_NAME_TOKEN || value === AUTHORIZATION_TOKEN) {
      value = "";
    }

    organized.push({ ...parameter, type, key, value });
  });

  return {
    parameters: organized,
    userNameHeaderParameterIndex,
    authTokenHeaderParameterIndex,
    basicAuthHeaderParameterIndex
  };
};

/**
 * The rows an auth type brings with it: Basic contributes the `Authorization` row, Bearer the
 * current-user row, and every type keeps whatever custom rows were already there — and the table
 * still ends on its blank draft row. The blank rows it is handed are dropped first, so the
 * generated row lands above the draft rather than under it.
 *
 * The copies this replaces re-numbered the ids through a `.map` whose result was discarded, so
 * rows kept whatever id they arrived with. Here the ids are actually sequential.
 */
export const headerParametersForAuthType = (
  authType: EAuthenticationType,
  existing: IApiKeyParameter[] | undefined
): IApiKeyParameter[] => {
  const customRows: IApiKeyParameter[] = withoutBlankRows(
    (existing ?? []).filter((parameter: IApiKeyParameter): boolean => parameter.type === PARAMETER_TYPE_CUSTOM),
    isBlankHeaderParameter
  );

  const rows: IApiKeyParameter[] = [...customRows];

  if (authType === EAuthenticationType.Basic) {
    rows.push({
      id: 0,
      type: PARAMETER_TYPE_BASIC_AUTH,
      key: AUTHORIZATION_HEADER_KEY,
      value: BASIC_AUTH_TOKEN
    });
  } else if (authType === EAuthenticationType.KuikaAuth) {
    rows.push({ id: 0, type: PARAMETER_TYPE_USERNAME, key: "", value: CURRENT_USER_NAME_TOKEN });
  }

  return ensureTrailingDraftRow(rows, isBlankHeaderParameter, blankHeaderParameter).map(
    (parameter: IApiKeyParameter, index: number): IApiKeyParameter => ({ ...parameter, id: index })
  );
};
